import React, { useEffect, useState, useContext, useRef, useMemo } from 'react';
import { AuthContext } from "../contexts/auth";
import { DocumentPlusIcon, MagnifyingGlassIcon, ChevronLeftIcon, ChevronRightIcon, InformationCircleIcon } from '@heroicons/react/24/outline';
import { LockClosedIcon, LockOpenIcon } from '@heroicons/react/20/solid';
import EditGiustificativoModal from './modal_edit_giustificativo';
import AddGiustificativoModal from './modal_add_giustificativo';
import { getCompanyAttendance } from '../services/api_calendar';
import { getJustificationsCompanyList } from '../services/api_justifications';
import { deleteJustificationRequest } from '../services/api_justification_requests';
import { exportEmployeeAttendance, exportCostRefunds } from '../services/api_extractions';
import { blockMonth, getMonthBlocked } from '../services/api_months_blocked';
import Swal from 'sweetalert2';
import { Link } from 'wouter';
import Fetching from './fetching';
import DayDetailsModal from './day_details_modal';
import CustomTooltipComponent from './custom_tooltip_component';
import { estraiMessaggiErrore } from '../services/utils';


const getLastDayOfCurrentMonth = () => {
  const date = new Date();
  const lastDay = new Date(date.getFullYear(), date.getMonth() + 1, 0);
  const day = String(lastDay.getDate()).padStart(2, '0');
  const month = String(lastDay.getMonth() + 1).padStart(2, '0');
  const year = lastDay.getFullYear();
  return `${day}/${month}/${year}`;
};

const daysOfWeek = ['D', 'L', 'M', 'M', 'G', 'V', 'S'];
const weekendDays = [0]; // Domenica

const justificationColors = {
  FE: '#fef3c7',
  PE: '#bfdbfe',
  RO: '#bfdbfe',
  PD: '#bfdbfe',
  EO: '#c4b5fd',
  MAL: '#fecaca',
  ST: '#fae8ff',
};
const getBackgroundColor = (code) => justificationColors[code] || '#f3f4f6';

const JUSTIFICATION_ROW_HEIGHT = 24;

const rateoColors = {
  'BANCA ORE': '#e0e7ff',
  'PERM. EX FESTIVITÀ': '#bfdbfe',
  'FERIE': '#fef3c7',
  'PERMESSI': '#bfdbfe',
  'ROL': '#bfdbfe',
};
const getRateoBackgroundColor = (name) => rateoColors[name] || '#f3f4f6';

export default function EmployeeAttendanceMonthTable({
  page,
  pageSize,
  setTotalResult,
  totalResult,
  setNextPage,
  setPrevPage,
  selectedUnit,
  onAbortControllerReady,
  selectedEmployees = [],
  date,
}) {
  
    
  const { token, profile } = useContext(AuthContext);

  // UI / stato generale
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);

  // dati
  const [attendanceData, setAttendanceData] = useState([]);
  const [dipendenti, setDipendenti] = useState([]);
  const [giustificativi, setGiustificativi] = useState([]);
  const [giustificativiMap, setGiustificativiMap] = useState({});
  const [holidays, setHolidays] = useState([]);

  // mese bloccato
  const [isMonthOpen, setIsMonthOpen] = useState(true);
  const [lastDayOfCurrentMonth, setLastDayOfCurrentMonth] = useState('');

  // modali / selezioni
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [selectedDay, setSelectedDay] = useState(null);
  const [showModalAddGiustificativo, setShowModalAddGiustificativo] = useState(false);

  const [selectedJustification, setSelectedJustification] = useState([]);

  const [isModalDetailsOpen, setIsModalDetailsOpen] = useState(false);
  const [modalDetailsData, setModalDetailsData] = useState();

  const [showRateiColumn, setShowRateiColumn] = useState(false);
  
  // export
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedFormat, setSelectedFormat] = useState('');
  const [isDownloadError, setIsDownloadError] = useState(false);

  // drag per scroll sincronizzato (opzionale, tenuto perché lo usi)
  const [isDragging, setIsDragging] = useState(false);
  const [isDayDragging, setIsDayDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);
  const tableContainerRef = useRef(null);

  const isEmployee = profile?.customer_type === 'employee';

  useEffect(() => {
    setLastDayOfCurrentMonth(getLastDayOfCurrentMonth());
  }, []);

  // mese ancora (1° del mese passato dal parent)
  const monthAnchor = useMemo(
    () => new Date(date.getFullYear(), date.getMonth(), 1),
    [date]
  );

    // chiavi stabili per deps
    const permKey   = useMemo(() => (profile?.employee?.permissions || []).join('|'), [profile?.employee?.permissions]);
    const custType  = profile?.customer_type;
    const selUnitId = selectedUnit?.id ?? null;

    const selectedEmpIds = useMemo(
        () => (selectedEmployees || []).map(e => (typeof e === 'object' ? e.id : e)),
        [selectedEmployees]
    );
    const empIdsKey = useMemo(() => selectedEmpIds.join(','), [selectedEmpIds]); // <-- parentesi chiuse

    const monthKey  = useMemo(
        () => `${monthAnchor.getFullYear()}-${monthAnchor.getMonth()}`,
        [monthAnchor]
    );

    // ...altri hook sopra
    const employeesToShow = useMemo(() => {
        if (selectedEmpIds.length === 0) return dipendenti;
        const setIds = new Set(selectedEmpIds.map(String)); // normalizza a stringhe
        return dipendenti.filter(d => setIds.has(String(d.id)));
    }, [dipendenti, selectedEmpIds]);

    // controller fetch + protezione contro loop su onAbortControllerReady
    const controller = useRef(null);
    const lastSentCtrl = useRef(null);

    useEffect(() => {
    if (controller.current) {
        controller.current.abort();
        setIsLoading(false);
    }
    controller.current = new AbortController();
    const signal = controller.current.signal;

    if (onAbortControllerReady && lastSentCtrl.current !== controller.current) {
        onAbortControllerReady(controller.current);
        lastSentCtrl.current = controller.current;
    }

    (async () => {
        if (!token) return;
        setIsLoading(true);
        setIsError(false);

        try {
        const startDate = formatDateMonthSelected(monthAnchor, 'start');
        const endDate   = formatDateMonthSelected(monthAnchor, 'end');

        // se ci sono dipendenti selezionati: niente paginazione e ignora OU
        const shouldPaginate = selectedEmpIds.length === 0;
        const unitFilter = shouldPaginate ? selUnitId : null;

        const res = await getCompanyAttendance(
            token,
            { startDate, endDate },
            unitFilter,
            selectedEmpIds,                        // <-- usa gli ID normalizzati
            shouldPaginate ? page : null,
            shouldPaginate ? pageSize : null,
            signal
        );

        // Paginazione
        setNextPage?.(res.next);
        setPrevPage?.(res.previous);
        setTotalResult?.(res.count);

        // Permessi di vista
        const requiredViewPermissions = ['hr.view_attendance_ou', 'hr.view_attendance_all'];
        const canViewAttendance =
            custType !== 'employee' ||
            requiredViewPermissions.some(p => profile?.employee?.permissions?.includes(p));

        // Dipendenti (lista per righe)
        if (res.results?.length) {
            setDipendenti(
            res.results.map(employee => ({
                first_name: employee.first_name,
                last_name: employee.last_name,
                cf: employee.cf,
                id: employee.id,
                email_auth: employee.email_auth,
                balances: employee.balances || [],
                days: employee.days || [],
            }))
            );
        } else {
            setDipendenti([]);
        }

        setAttendanceData(canViewAttendance ? (res.results || []) : []);

        // Festivi raccolti da days
        const holidaysData = [];
        res.results?.forEach(employee => {
            employee.days?.forEach(day => {
            if (day.holidays?.length) {
                day.holidays.forEach(h => {
                holidaysData.push({ date: day.date, name: h.name });
                });
            }
            });
        });
        setHolidays(holidaysData);

        // Giustificativi disponibili + mappa
        const requiredManage = ['hr.manage_justification_all', 'hr.manage_justification_ou'];
        const canManageJust = custType !== 'employee' ||
            requiredManage.some(p => profile?.employee?.permissions?.includes(p));

        if (canManageJust) {
            const giustificativiData = await getJustificationsCompanyList(token);
            setGiustificativi(giustificativiData);
            const map = giustificativiData.reduce((acc, curr) => {
            acc[curr.justification.code] = curr.justification;
            return acc;
            }, {});
            setGiustificativiMap(map);
        } else {
            setGiustificativi([]);
            setGiustificativiMap({});
        }

        // mese bloccato?
        const blocked = await getMonthBlocked(token, { startDate, endDate });
        const isBlocked = blocked.some(m => m.year === monthAnchor.getFullYear() && m.month === (monthAnchor.getMonth() + 1));
        setIsMonthOpen(!isBlocked);

        setIsLoading(false);
        } catch (err) {
        if (err?.name !== 'CanceledError') {
            console.error('Errore durante il caricamento dei dati:', err);
            setIsError(true);
            setIsLoading(false);
        }
        }
    })();

    return () => controller.current?.abort();
    }, [token, monthKey, empIdsKey, selUnitId, page, pageSize, custType, permKey]);

  // —— Azioni

  const formatDateMonthSelected = (d, type) => {
    const year = d.getFullYear();
    const month = (d.getMonth() + 1).toString().padStart(2, '0');
    const day = type === 'start'
      ? '01'
      : new Date(year, d.getMonth() + 1, 0).getDate().toString().padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const handleShowMessage = () => {
    const selectedMonthValue = monthAnchor.getMonth() + 1;
    const selectedYearValue = monthAnchor.getFullYear();

    Swal.fire({
      title: 'Sei sicuro?',
      text: 'Bloccando il mese non potranno più essere effettuate modifiche sulle presenze. Operazione irreversibile. Confermi?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Conferma',
      cancelButtonText: 'Annulla',
      cancelButtonColor: '#d1d5db',
    }).then(async (result) => {
      if (!result.isConfirmed) return;
      try {
        await blockMonth(token, selectedMonthValue, selectedYearValue);
        setIsMonthOpen(false);
        Swal.fire('Bloccato!', 'Il mese è stato bloccato.', 'success');
      } catch (error) {
        console.error('Errore blocco mese:', error);
        Swal.fire('Errore!', 'Si è verificato un errore durante il blocco del mese.', 'error');
      }
    });
  };

  const handleExport = async (format, unitId) => {
    setIsDownloadError(false);

    const startDate = formatDateMonthSelected(monthAnchor, 'start');
    const endDate = formatDateMonthSelected(monthAnchor, 'end');
    const employeeIds = selectedEmpIds;

    if (!startDate || !endDate) {
      Swal.fire({
        icon: "warning",
        title: "Intervallo di date non valido",
        text: "Seleziona un mese valido prima di procedere.",
      });
      return;
    }

    try {
      const { status, data } = await exportEmployeeAttendance(
        token,
        startDate,
        endDate,
        format,
        unitId,
        employeeIds
      );

      const alreadyExists =
        status === 200 &&
        String(data?.detail || "").toLowerCase().includes("già");

      Swal.fire({
        icon: alreadyExists ? "warning" : "success",
        title: alreadyExists ? "Esportazione già in corso" : "Esportazione avviata",
        html: `
          <div>
            ${
              alreadyExists
                ? "Esiste già un export per gli stessi parametri."
                : "Il file verrà generato in background e sarà scaricabile quando pronto."
            }
            <br/>
            <a href="/download_list" style="color:#4f46e5; text-decoration:underline;">
              Clicca qui
            </a>
            per andare alla pagina download.
          </div>
        `,
        confirmButtonText: "Chiudi",
      });
    } catch (error) {
      setIsDownloadError(true);

      const msg =
        error?.response?.data?.detail ||
        error?.message ||
        estraiMessaggiErrore(error) ||
        "Errore, riprova più tardi.";

      Swal.fire({
        icon: "error",
        title: "Errore durante l’esportazione",
        text: msg,
        confirmButtonText: "OK",
      });
    }
  };

  const handleExportCostRefunds = async () => {
    setIsLoading(true);
    setIsError(false);

    const month = monthAnchor.getMonth() + 1;
    const year = monthAnchor.getFullYear();

    try {
      const { status, data } = await exportCostRefunds(token, month, year);

      const alreadyExists =
        status === 200 &&
        String(data?.detail || "").toLowerCase().includes("già");

      Swal.fire({
        icon: alreadyExists ? "warning" : "success",
        title: alreadyExists ? "Esportazione già in corso" : "Esportazione avviata",
        html: `
          <div>
            ${
              alreadyExists
                ? "Esiste già un export per gli stessi parametri."
                : "Il file verrà generato in background e sarà scaricabile quando pronto."
            }
            <br/>
            <a href="/download_list" style="color:#4f46e5; text-decoration:underline;">
              Clicca qui
            </a>
            per andare alla pagina download.
          </div>
        `,
        confirmButtonText: "Chiudi",
      });

    } catch (error) {
      const status = error?.response?.status;
      const detail = error?.response?.data?.detail;

      if (status === 404) {
        Swal.fire({
          icon: "info",
          title: "Nessun rimborso",
          text: detail || "Nessun rimborso spesa trovato per il periodo selezionato.",
          confirmButtonText: "OK",
        });
        return;
      }

      setIsError(true);

      const msg =
        detail ||
        error?.message ||
        estraiMessaggiErrore(error) ||
        "Errore, riprova più tardi.";

      Swal.fire({
        icon: "error",
        title: "Errore durante l’esportazione",
        text: msg,
        confirmButtonText: "OK",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpenDetailsModal = (dipendente, formattedDay) => {
    if (!dipendente) return;
    setSelectedEmployee(dipendente);
    setModalDetailsData(formattedDay);
    setIsModalDetailsOpen(true);
  };


  const openModalAddGiustificativo = (dipendente, formattedDay, dayData) => {
    setShowModalAddGiustificativo(true);
    setSelectedEmployee(dipendente);
    setSelectedDay(formattedDay);
    setSelectedJustification(dayData);
  };

  // — drag orizzontale tabella
  const startDragging = (e) => {
    setIsDragging(true);
    setStartX(e.clientX);
    setScrollLeft(tableContainerRef.current?.scrollLeft || 0);
  };
  const startDayDragging = (e) => {
    setIsDayDragging(true);
    setIsDragging(true);
    setStartX(e.clientX);
    setScrollLeft(tableContainerRef.current?.scrollLeft || 0);
  };
  const stopDragging = () => {
    setIsDragging(false);
    setIsDayDragging(false);
  };
  const onDragging = (e) => {
    if (!isDragging) return;
    const x = e.clientX - startX;
    if (tableContainerRef.current) tableContainerRef.current.scrollLeft = scrollLeft - x;
  };
  useEffect(() => {
    if (isDragging || isDayDragging) {
      document.addEventListener('mousemove', onDragging);
      document.addEventListener('mouseup', stopDragging);
    } else {
      document.removeEventListener('mousemove', onDragging);
      document.removeEventListener('mouseup', stopDragging);
    }
    return () => {
      document.removeEventListener('mousemove', onDragging);
      document.removeEventListener('mouseup', stopDragging);
    };
  }, [isDragging, isDayDragging]);

  // — mappa rapida per accesso attendance per id
  const attendanceDataById = useMemo(
    () =>
      Array.isArray(attendanceData)
        ? attendanceData.reduce((acc, emp) => {
            acc[emp.id] = emp;
            return acc;
          }, {})
        : {},
    [attendanceData]
  );

  // giustificativi per ordinare le righe “pillole”
  const justificationTypes = useMemo(() => {
    const types = new Set();
    attendanceData?.forEach(emp => {
      emp?.days?.forEach(day => {
        day?.justifications?.forEach(j => types.add(j.code));
      });
    });
    return Array.from(types).sort();
  }, [attendanceData]);

  const daysInMonth = useMemo(
    () => new Date(monthAnchor.getFullYear(), monthAnchor.getMonth() + 1, 0).getDate(),
    [monthAnchor]
  );

  {isError && (
    <div className="p-4 text-red-600 text-sm">
      Errore durante l’operazione.
    </div>
  )}

  return (
    <div className='bg-gray-100'>
      {/* Header stato mese + export */}
      <div className="grid sm:grid-cols-1 md:grid-cols-2 w-full border-x border-gray-200 justify-center md:justify-between bg-gray-100">
        {isLoading ? (
          <div className='p-2 text-start text-sm font-semibold text-gray-600'>Caricamento...</div>
        ) : (
          <div className="col-span-1 p-2 flex flex-col items-start text-sm uppercase text-gray-500 ml-2 me-3 mt-2">
            <div className="flex items-center font-bold">
              Stato mese: {isMonthOpen ? 'aperto' : 'chiuso'}
              {isMonthOpen ? (
                <LockOpenIcon className="w-5 h-5 ml-2 text-indigo-500" />
              ) : (
                <LockClosedIcon className="w-5 h-5 ml-2 text-indigo-500" />
              )}
            </div>

            {isMonthOpen && !isEmployee && (
              <button
                type="button"
                onClick={handleShowMessage}
                className="w-36 mt-2 inline-flex items-center justify-center rounded-lg bg-white px-3 py-1.5 text-xs font-semibold text-indigo-600 border border-indigo-600 shadow-sm hover:bg-indigo-100 disabled:opacity-40"
              >
                BLOCCA MESE
              </button>
            )}
          </div>
        )}

        <div className="sm:col-span-1 md:col-span-1 flex flex-col justify-end md:justify-end md:flex-row items-center mt-2 md:mt-4 md:me-4 me-0">
          <div className="relative">
            <div className='flex flex-col space-y-2'>
              <button
                type="button"
                onClick={() => setShowDropdown(!showDropdown)}
                disabled={isLoading}
                className="w-auto inline-flex items-center justify-center rounded-lg bg-indigo-600 px-3 py-1.5 text-xs font-semibold text-white shadow-sm hover:bg-indigo-500 disabled:opacity-40"
              >
                {isLoading ? 'Esportazione...' : 'ESPORTA PRESENZE'}
              </button>

              <button
                type="button"
                onClick={handleExportCostRefunds}
                disabled={isLoading}
                className="w-auto inline-flex items-center justify-center rounded-lg bg-indigo-600 px-3 py-1.5 text-xs font-semibold text-white shadow-sm hover:bg-indigo-500 disabled:opacity-40"
              >
                {isLoading ? 'Esportazione...' : 'ESPORTA RIMBORSI SPESE'}
              </button>
            </div>

            {showDropdown && (
              <div
                className="absolute mt-1 w-40 border border-gray-300 shadow-md rounded-md bg-white z-50 w-full"
                style={{ top: '40%' }}
              >
                <button
                  onClick={() => { setSelectedFormat('txt'); handleExport('txt', selectedUnit?.id); setShowDropdown(false); }}
                  className='block w-full text-left px-4 py-2 hover:bg-indigo-100 w-full'
                >
                  .txt
                </button>
                <button
                  onClick={() => { setSelectedFormat('txt-inaz'); handleExport('txt-inaz', selectedUnit?.id); setShowDropdown(false); }}
                  className='block w-full text-left px-4 py-2 hover:bg-indigo-100 w-full'
                >
                  .txt (INAZ)
                  <span className='text-indigo-500 text-xs font-bold ml-1'>BETA</span>
                </button>
                <button
                  onClick={() => { setSelectedFormat('excel'); handleExport('excel', selectedUnit?.id); setShowDropdown(false); }}
                  className='block w-full text-left px-4 py-2 hover:bg-indigo-100 w-full'
                >
                  Excel
                </button>
                <button
                  onClick={() => { setSelectedFormat('trripw'); handleExport('trripw', selectedUnit?.id); setShowDropdown(false); }}
                  className='block w-full text-left px-4 py-2 hover:bg-indigo-100 w-full'
                >
                  TRRIPW (xml)
                </button>
                <button
                  onClick={() => { setSelectedFormat('jlp'); handleExport('jlp', selectedUnit?.id); setShowDropdown(false); }}
                  className='block w-full text-left px-4 py-2 hover:bg-indigo-100 w-full'
                >
                  JOB (jlp) 
                  {/* <span className='text-indigo-500 text-xs font-bold'>BETA</span> */}
                </button>
                <button
                  onClick={() => { setSelectedFormat('csv'); handleExport('csv', selectedUnit?.id); setShowDropdown(false); }}
                  className='block w-full text-left px-4 py-2 hover:bg-indigo-100 w-full'
                >
                  CSV (Giotto) 
                  {/* <span className='text-indigo-500 text-xs font-bold'>BETA</span> */}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className='bg-gray-100'>
            <h2 className='p-2 text-start text-xs border-x font-semibold text-gray-600'>* Seleziona con il mouse tenendo premuto un qualsiasi punto della tabella e trascina a destra o sinistra per scorrere i giorni</h2>
      </div>

      {isLoading ? (
        <div className='bg-gray-100 pb-2'>
          <Fetching />
        </div>
      ) : (
        <>
          {/* TABELLA */}
          <div ref={tableContainerRef}
            style={{ overflow: 'auto', maxHeight: '70vh',
              cursor: isDragging || isDayDragging ? 'grabbing' : 'grab',
              scrollbarGutter: 'stable',
            }}
            onMouseDown={startDragging}
            onMouseUp={stopDragging}
            onMouseMove={onDragging}
          >
            <table className="min-w-full border-collapse border divide-y divide-gray-200">
              <thead
                style={{
                  background: 'white',
                  border: '2px solid #e5e7eb',
                  minWidth: '7rem',
                  cursor: isDragging || isDayDragging ? 'grabbing' : 'grab',
                  maxWidth: '100%',
                }}
                onMouseDown={startDragging}
              >
                  <tr>
                    <th
                      style={{
                        position: 'sticky',
                        top: 0,
                        left: 0,
                        background: 'white',
                        zIndex: 4,
                        minWidth: '10rem',
                        maxWidth: '10rem',
                      }}
                      className="px-2 py-3 border-2 border-gray-200 ring-1 ring-inset ring-gray-300 text-left text-xs bg-white font-semibold text-gray-600 uppercase w-28">
                      Dipendenti
                    </th>

                    {Array.from({ length: daysInMonth }).map((_, index) => {
                      const dayNumber = index + 1;
                      const dayOfWeek = new Date(
                        monthAnchor.getFullYear(),
                        monthAnchor.getMonth(),
                        dayNumber
                      ).getDay();

                      const dayOfWeekInitial = daysOfWeek[dayOfWeek];
                      const isWeekend = weekendDays.includes(dayOfWeek);

                      const formattedDate = `${monthAnchor.getFullYear()}-${(monthAnchor.getMonth() + 1)
                        .toString()
                        .padStart(2, '0')}-${dayNumber.toString().padStart(2, '0')}`;
                      const holidayInfos = holidays.filter(h => h.date === formattedDate);
                      const holidayNames = Array.from(new Set(holidayInfos.map(h => h.name))).join(', ');

                      const textColorClass =
                        holidayInfos.length > 0
                          ? 'text-orange-400 font-bold text-sm'
                          : isWeekend
                          ? 'text-emerald-500 font-bold text-sm'
                          : 'text-gray-600 text-xs';

                      const handleInfoHoliday = () => {
                        if (holidayInfos.length > 0) {
                          Swal.fire({ title: 'Festività', html: `<p>${holidayNames}</p>`, confirmButtonText: 'OK' });
                        }
                      };

                      return (
                        <th
                          key={index}
                          className={`py-1 px-4 border-2 border-gray-200 ring-1 ring-inset ring-gray-300 text-center w-24 font-semibold uppercase ${textColorClass}`}
                          style={{
                            position: 'sticky',
                            top: 0,
                            background: 'white',
                            zIndex: 3,
                            minWidth: '3rem',
                            maxWidth: '3rem',
                            cursor: holidayInfos.length > 0
                              ? 'pointer'
                              : isDayDragging
                                ? 'grabbing'
                                : 'grab',
                          }}
                          onClick={handleInfoHoliday}
                          onMouseDown={startDayDragging}
                        >
                          <div>
                            {dayNumber}
                            <div className={`pt-2 ${textColorClass}`}>
                              {dayOfWeekInitial}
                            </div>
                          </div>
                        </th>
                      );
                    })}

                    <th
                      style={{
                        position: 'sticky',
                        top: 0,
                        right: showRateiColumn ? '8.8rem' : 0,
                        background: 'white',
                        zIndex: 4,
                        minWidth: '9rem',
                        maxWidth: '9rem',
                      }}
                      className="px-2 py-2 border-2 border-gray-200 ring-1 ring-inset ring-gray-300 text-center bg-white"
                    >
                      <div className="flex flex-col items-center justify-center gap-1.5">
                        <span className="text-xs font-bold tracking-wide text-gray-600 uppercase">
                          Totali ore mese
                        </span>

                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            setShowRateiColumn(prev => !prev);
                          }}
                          onMouseDown={(e) => e.stopPropagation()}
                          className={`
                            inline-flex items-center justify-center gap-1.5
                            px-2.5 py-0.5 rounded-md border
                            text-[10px] font-semibold uppercase tracking-wide
                            transition-colors duration-150
                            ${showRateiColumn
                              ? 'bg-indigo-100 border-indigo-200 text-indigo-700 hover:bg-indigo-200'
                              : 'bg-indigo-50 border-indigo-100 text-indigo-600 hover:bg-indigo-100'
                            }
                          `}
                          title={showRateiColumn ? 'Nascondi residuo ratei' : 'Mostra residuo ratei'}
                          aria-expanded={showRateiColumn}
                        >
                          {showRateiColumn ? (
                            <ChevronRightIcon className="w-3.5 h-3.5" />
                          ) : (
                            <ChevronLeftIcon className="w-3.5 h-3.5" />
                          )}
                          <span>Ratei</span>
                        </button>
                      </div>
                    </th>

                    {showRateiColumn && (
                        <th
                            style={{
                                position: 'sticky',
                                top: 0,
                                right: 0,
                                background: 'white',
                                zIndex: 4,
                                borderLeft: '2px solid #e5e7eb',
                                minWidth: '9rem',
                                maxWidth: '9rem',
                            }}
                            className="pr-4 py-3 px-2 border-2 border-gray-200 text-center text-xs w-28 font-semibold text-gray-600 uppercase ring-1 ring-inset ring-gray-300"
                        >
                            Residuo ratei al <br /> {lastDayOfCurrentMonth}
                        </th>
                    )}
                  </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                    {(() => {
                        if (!isLoading && employeesToShow.length === 0) {
                            return (
                                <tr>
                                    <td colSpan={33} className="pl-4 py-3 border-2 border-gray-200 bg-white text-left italic text-sm font-medium text-gray-600 ">
                                        Non sono presenti anagrafiche
                                    </td>
                                </tr>
                            );
                    }

                    return employeesToShow.map((dipendente, rowIndex) => {
                      const empData = attendanceDataById[dipendente.id];
                        const employeeJustificationTypes = Array.from(
                          new Set(
                            (empData?.days || []).flatMap(day =>
                              (day.justifications || []).map(j => j.code)
                            )
                          )
                        ).sort();
                        
                      return (
                        <tr key={rowIndex}>
                          {/* Colonna dipendente (sticky) */}
                          <td
                            style={{
                              position: 'sticky',
                              left: 0,
                              background: 'white',
                              zIndex: 1,
                              minWidth: '10rem',
                              maxWidth: '10rem',
                            }}
                            className="px-2 py-3 border-2 border-gray-200 ring-1 ring-inset ring-gray-300 text-left text-xs bg-white font-semibold text-gray-600 uppercase w-28"
                            title={dipendente.first_name ? `${dipendente.last_name} ${dipendente.first_name}` : dipendente.cf}
                          >
                            <Link to={`/dipendenti/${dipendente.cf}`} className="hover:text-indigo-600">
                              {dipendente.first_name
                                ? `${dipendente.last_name} ${dipendente.first_name}`.slice(0, 25)
                                : dipendente.cf}
                              {dipendente.first_name &&
                                `${dipendente.last_name} ${dipendente.first_name}`.length > 25
                                ? '...'
                                : ''}
                            </Link>
                          </td>

                          {/* Giorni */}
                          {Array.from({ length: daysInMonth }).map((_, i) => {
                            const dayData = empData ? empData.days[i] : null;

                            const weekHours = dayData ? dayData.week_hours : null;
                            const shiftType = dayData ? dayData.shift_type : null;

                            let canAddJustification = (
                                shiftType === 'a_chiamata'
                                || (shiftType === 'turno' && weekHours && weekHours > 0)
                                || (shiftType === 'fisso' && weekHours && weekHours > 0)
                            );

                            const hasActiveContract = dayData?.has_active_contract;
                            let hours = '-';
                            let justifications = [];

                            if (dayData) {
                              // Recupera sempre i giustificativi, indipendentemente
                              // dalla tipologia di calendario
                              justifications = dayData.justifications || [];

                              // Mantiene invariata la logica delle ore previste
                              if (
                                dayData.shift_type !== null &&
                                (dayData.week_hours || dayData.shift_type === 'a_chiamata')
                              ) {
                                let attendanceHours = dayData.attendance_expected / 60;
                                const formattedHours = Number.isInteger(attendanceHours)
                                  ? attendanceHours.toFixed(0)
                                  : attendanceHours.toFixed(2);

                                hours = formattedHours;
                              }
                            }

                            const year = monthAnchor.getFullYear();
                            const month = monthAnchor.getMonth() + 1;
                            const dayOfMonth = i + 1;

                            const formattedDay = `${year}-${month < 10 ? '0' + month : month}-${dayOfMonth < 10 ? '0' + dayOfMonth : dayOfMonth}`;
                            const isWeekend = weekendDays.includes(new Date(year, month - 1, dayOfMonth).getDay());
                            const isHoliday = holidays.find(holiday => holiday.date === formattedDay);
                            const isHolidayWeekend = isHoliday && isWeekend;

                            const hasJustifications = justifications.length > 0;

                            const showNoWeekHoursPattern = (!weekHours && shiftType && shiftType !== 'senza_calendario' && shiftType !== 'a_chiamata');

                            return (
                                <td
                                    key={i}
                                    className={`border-2 border-gray-200 text-center text-sm font-semibold uppercase p-0`}
                                    style={{
                                        minWidth: '3rem',
                                        maxWidth: '3rem',
                                        position: 'relative',
                                        padding: 0,
                                        verticalAlign: 'top',
                                        paddingBottom: '1rem',
                                        minHeight: '2rem',
                                        height: '3.7rem',
                                        ...((isHoliday && weekHours && shiftType !== 'senza_calendario') ? SvgHoliday() : {}),
                                        ...((isHolidayWeekend && weekHours && shiftType !== 'senza_calendario') ? SvgHolidayWeekend() : {}),
                                        ...((isWeekend && !isHoliday && weekHours && shiftType !== 'senza_calendario') ? SvgPattern() : {}),
                                        ...((shiftType === 'senza_calendario') && PatternNoCalendar()),
                                        ...(showNoWeekHoursPattern && PatternNoWeekHours()),
                                    }}
                                    onClick={() => {
                                        // if (!isMonthOpen) {
                                        //     Swal.fire({
                                        //         title: 'Mese bloccato',
                                        //         text: 'Il mese è bloccato. Modifiche non consentite.',
                                        //         icon: 'error',
                                        //         confirmButtonText: 'Ok',
                                        //     });
                                        //     return;
                                        // }
                                        if (!hasActiveContract) {
                                            Swal.fire({
                                              title: 'Contratto non attivo',
                                              html: `Il dipendente non ha un contratto attivo.<br /> Vai su <a href="/dipendenti/${dipendente.cf}/edit?tab=2" style="color: #4338ca; text-decoration: underline;">MODIFICA CONTRATTO</a> per salvare un nuovo contratto.`,
                                              icon: 'info',
                                              confirmButtonText: 'Ok',
                                            });
                                        } else if (hasActiveContract && !weekHours && shiftType !== 'a_chiamata') {
                                            Swal.fire({
                                                title: 'Orario settimanale mancante',
                                                html: `Il dipendente non ha orario lavorativo settimanale.<br /> Vai su <a href="/dipendenti/${dipendente.cf}/edit?tab=2" style="color: #4338ca; text-decoration: underline;">MODIFICA CONTRATTO</a> per salvare l'orario.`,
                                                icon: 'info',
                                                confirmButtonText: 'Ok',
                                            });
                                        }
                                    }}
                                >
                                {/* ore previste */}
                                <div
                                    className="align-top"
                                    style={{
                                        position: 'absolute',
                                        top: '10px',
                                        width: '100%',
                                        display: hours !== '0' ? 'flex' : 'none',
                                        justifyContent: 'center',
                                        backgroundColor: !['0', '-'].includes(hours)  ? '#d1fae5' : 'transparent',
                                        ...(!hasActiveContract && PatternInactiveEmployee()),
                                    }}
                                >
                                  <span className="px-1">
                                    {hours === '-' ? <span className='text-transparent'>-</span> : hours}
                                  </span>
                                </div>

                                {/* pillole giustificativi */}
                                {employeeJustificationTypes.length > 0 && (
                                  <div
                                    className="flex flex-col"
                                    style={{ marginTop: '38px', marginBottom: '8px' }}
                                  >
                                    {employeeJustificationTypes.map((type, idx2) => {
                                      const jsOfType = justifications.filter(j => j.code === type);

                                      if (!jsOfType.length) return (
                                        <div key={`empty-${idx2}`} style={{ height: `${JUSTIFICATION_ROW_HEIGHT}px` }} />
                                      );

                                      const totalHours = jsOfType.reduce((sum, j) => sum + (j.duration || 0), 0) / 60;
                                      let formatted = Number.isInteger(totalHours) ? totalHours.toFixed(0) : totalHours.toFixed(2);
                                      if (formatted.endsWith('.00')) formatted = String(parseInt(formatted, 10));

                                      const bg = jsOfType.some(j => j.status === 'approved')
                                        ? shiftType === 'senza_calendario'
                                          ? '#d6d3d1'
                                          : getBackgroundColor(type)
                                        : 'white';

                                      const hasOuPair = jsOfType.some(j => j.ou);

                                      return (
                                        <div key={`${type}-${idx2}`} className="flex items-center justify-center" style={{ height: `${JUSTIFICATION_ROW_HEIGHT}px` }}>
                                          <CustomTooltipComponent title={hasOuPair && jsOfType[0]?.ou_name} placement="top-start">
                                            <span
                                              className={`cursor-pointer relative w-full text-gray-700 flex items-center justify-center ${formatted === '0' ? 'bg-white border-y border-gray-200 shadow-sm' : ''}`}
                                              style={{ backgroundColor: bg, height: `${JUSTIFICATION_ROW_HEIGHT - 2}px` }}
                                              onClick={(e) => { e.stopPropagation(); handleOpenDetailsModal(dipendente, formattedDay); }}
                                            >
                                              {formatted !== '0' ? formatted : '-'}
                                              {hasOuPair && (
                                                <div
                                                  className="absolute top-0 right-0"
                                                  style={{
                                                    width: 0,
                                                    height: 0,
                                                    borderLeft: '10px solid transparent',
                                                    borderTop: '10px solid gray',
                                                  }}
                                                />
                                              )}
                                            </span>
                                          </CustomTooltipComponent>
                                        </div>
                                      );
                                    })}
                                  </div>
                                )}

                                {/* azioni in basso cella */}
                                {hasActiveContract && (
                                  <div>
                                    <div
                                      className="bottom-0 cursor-pointer border-t bg-white"
                                      style={{
                                        position: 'absolute',
                                        bottom: 0,
                                        width: '100%',
                                        textAlign: 'center',
                                        display: 'flex',
                                        justifyContent: 'center',
                                        alignItems: 'center',
                                        gap: '4px',
                                        padding: '2px 0',
                                      }}
                                    >
                                      <CustomTooltipComponent title='Visualizza dettagli'>
                                        <MagnifyingGlassIcon
                                          className="h-4 w-4 text-gray-400 hover:text-indigo-500 hover:bg-indigo-200"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            const required = [
                                              'hr.manage_justification_all',
                                              'hr.manage_justification_ou',
                                              'hr.view_shifts_all',
                                              'hr.view_shifts_ou',
                                              'hr.view_inouts_all',
                                              'hr.view_inouts_ou'
                                            ];
                                            const hasPerm =
                                              profile?.customer_type !== 'employee' ||
                                              required.some(p => profile?.employee?.permissions?.includes(p));

                                            if (!hasPerm) {
                                              Swal.fire({
                                                icon: 'warning',
                                                title: 'Non hai i permessi per eseguire questa azione',
                                                showConfirmButton: true,
                                                confirmButtonText: 'OK',
                                              });
                                            } else {
                                              handleOpenDetailsModal(dipendente, formattedDay);
                                            }
                                          }}
                                        />
                                      </CustomTooltipComponent>

                                      {isMonthOpen && (
                                        <CustomTooltipComponent title='Aggiungi giustificativo' placement='top' arrow>
                                          <DocumentPlusIcon
                                            className="h-4 w-4 text-gray-400 hover:text-indigo-500 hover:bg-indigo-200"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              const required = ['hr.manage_justification_all', 'hr.manage_justification_ou'];
                                              const hasPerm =
                                                profile?.customer_type !== 'employee' ||
                                                required.some(p => profile?.employee?.permissions?.includes(p));

                                              if (!hasPerm) {
                                                Swal.fire({
                                                  icon: 'warning',
                                                  title: 'Non hai i permessi per eseguire questa azione',
                                                  showConfirmButton: true,
                                                  confirmButtonText: 'OK',
                                                });
                                              } else {
                                                openModalAddGiustificativo(dipendente, formattedDay, dayData);
                                              }
                                            }}
                                          />
                                        </CustomTooltipComponent>
                                      )}
                                    </div>
                                  </div>
                                )}
                              </td>
                            );
                          })}

                          {/* Colonna Totali */}
                          <td
                            style={{
                              position: 'sticky',
                              right: showRateiColumn ? '8.8rem' : 0,
                              background: 'white',
                              zIndex: 1,
                              border: '2px solid #e5e7eb',
                              minWidth: '9rem',
                              maxWidth: '9rem',
                              verticalAlign: 'top',
                              padding: 0,
                            }}
                            className="text-center text-xs font-semibold bg-gray-50 text-gray-600 border-2 border-gray-200 ring-1 ring-inset ring-gray-300"
                          >
                            <div className="absolute inset-x-1 top-1 bottom-1 px-1 shadow-md border border-indigo-200 rounded-md bg-white text-gray-900">
                              {/* Riepilogo presenze */}
                              <span className='items-start flex text-[11px] mb-[-5px]'>Presenza</span>
                              <div className="flex items-center justify-between h-6 w-full text-xs font-semibold">
                                <CustomTooltipComponent title="Prevista | Effettiva" placement="top" arrow>
                                  <span className="inline-flex items-center cursor-help">
                                    <InformationCircleIcon className="h-4 w-4 text-gray-400 hover:text-indigo-600" />
                                  </span>
                                </CustomTooltipComponent>

                                <span className="bg-emerald-100 text-gray-900 px-1">
                                  {empData && (() => {
                                    const tot = (empData.days || []).reduce((sum, d) => sum + (d.attendance_expected || 0), 0) / 60;
                                    return Number.isInteger(tot) ? tot.toFixed(0) : tot.toFixed(2);
                                  })()}
                                </span>
                                <span>|</span>
                                <span className="bg-emerald-100 text-gray-900 px-1">
                                  {empData && (() => {
                                    const tot = (empData.days || []).reduce((sum, d) => sum + (d.attendance_actual || 0), 0) / 60;
                                    return Number.isInteger(tot) ? tot.toFixed(0) : tot.toFixed(2);
                                  })()}
                                </span>
                              </div>

                              {/* Totali giustificativi */}
                              {empData && (() => {
                                const totals = {};
                                (empData.days || []).forEach(d => {
                                  (d.justifications || []).forEach(j => {
                                    if (j.status === 'approved') {
                                      totals[j.code] = (totals[j.code] || 0) + (j.duration || 0);
                                    }
                                  });
                                });

                                return employeeJustificationTypes.map(type => {
                                  const h = (totals[type] || 0) / 60;
                                  let formatted = Number.isInteger(h) ? h.toFixed(0) : h.toFixed(2);
                                  if (formatted.endsWith('.00')) formatted = String(parseInt(formatted, 10));
                                  const bg = getBackgroundColor(type);
                                  const details = giustificativiMap[type] || {};
                                  const full = details.description || type;
                                  let short = full;
                                  if (short.length > 10) short = short.substring(0, 10) + '.';
                                  return (
                                    <div
                                      key={type}
                                      className="flex justify-between items-center text-xs font-semibold"
                                      style={{ height: `${JUSTIFICATION_ROW_HEIGHT}px` }}
                                    >
                                      <CustomTooltipComponent title={full} placement="left">
                                        <span className="truncate cursor-help">{short}</span>
                                      </CustomTooltipComponent>

                                      <span className="text-center min-w-4" style={{ backgroundColor: bg }}>
                                        {totals[type] ? (formatted !== '0' ? formatted : '-') : '-'}
                                      </span>
                                    </div>
                                  );
                                });
                              })()}
                            </div>
                          </td>

                          {/* Colonna Residui */}
                          {showRateiColumn && (
                            <td
                              style={{
                                position: 'sticky',
                                right: 0,
                                background: 'white',
                                zIndex: 1,
                                borderLeft: '2px solid #e5e7eb',
                                minWidth: '9rem',
                                maxWidth: '9rem',
                                verticalAlign: 'top',
                                height: '100%',
                              }}
                              className="px-1 text-start text-xs font-semibold bg-gray-50 text-gray-600 h-full border-2 border-gray-200 ring-1 ring-inset ring-gray-300"
                            >
                              <div
                                className={`px-1 flex flex-col justify-start py-1 mt-1 ${empData?.balances?.length > 0 ? 'shadow-md border border-indigo-200 rounded-md' : ''}`}
                                style={{ display: empData?.balances?.length > 0 ? 'block' : 'none' }}
                              >
                              
                                {empData?.balances?.map((b, bi) => (
                                  <div key={bi} className="flex justify-between text-gray-900 cursor-pointer hover:underline">
                                    <Link to={`/dipendenti/${dipendente.cf}?tab=residui`} className="hover:text-indigo-600 uppercase">
                                      <span>{b.rateo.name}</span>
                                    </Link>
                                    <Link to={`/dipendenti/${dipendente.cf}/`} className="hover:text-indigo-600 uppercase">
                                      <span style={{ backgroundColor: getRateoBackgroundColor(b.rateo.name) }}>
                                        {(() => {
                                          const hours = (b.value || 0) / 60;
                                          return Number.isInteger(hours) ? hours.toFixed(0) : hours.toFixed(2);
                                        })()}
                                      </span>
                                    </Link>
                                  </div>
                                ))}
                              </div>
                            </td>
                          )}
                        </tr>
                      );
                    });
                  })()}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* MODALI */}

      {showModalAddGiustificativo && (
        <AddGiustificativoModal
          closeModalAddGiustificativi={() => setShowModalAddGiustificativo(false)}
          dipendente={selectedEmployee}
          formattedDay={selectedDay}
          selectedJustification={selectedJustification}
          onSaveJustificationRequest={(newReq, employeeId, formattedDay) => {
            // dopo il salvataggio ricarico il mese (semplice/robusto)
            (async () => {
              const startDate = formatDateMonthSelected(monthAnchor, 'start');
              const endDate = formatDateMonthSelected(monthAnchor, 'end');
              const res = await getCompanyAttendance(token, { startDate, endDate }, selectedUnit?.id);
              setAttendanceData(res?.results || []);
            })();
          }}
          selectedUnit={selectedUnit}
        />
      )}

      {isModalDetailsOpen && (
        <DayDetailsModal
          isOpen={isModalDetailsOpen}
          onClose={() => setIsModalDetailsOpen(false)}
          formattedDay={modalDetailsData}
          employee={selectedEmployee}
          onNewJustificationAdded={() => {
            // ricarico il mese dopo aggiunta da modal dettagli
            (async () => {
              const startDate = formatDateMonthSelected(monthAnchor, 'start');
              const endDate = formatDateMonthSelected(monthAnchor, 'end');
              const res = await getCompanyAttendance(token, { startDate, endDate }, selectedUnit?.id);
              setAttendanceData(res?.results || []);
            })();
          }}
          onJustificationChanged={() => {
            // ricarico il mese dopo modifica da modal dettagli
            (async () => {
              const startDate = formatDateMonthSelected(monthAnchor, 'start');
              const endDate = formatDateMonthSelected(monthAnchor, 'end');
              const res = await getCompanyAttendance(token, { startDate, endDate }, selectedUnit?.id);
              setAttendanceData(res?.results || []);
            })();
          }}
          onOUChanged={() => {
            // idem
            (async () => {
              const startDate = formatDateMonthSelected(monthAnchor, 'start');
              const endDate = formatDateMonthSelected(monthAnchor, 'end');
              const res = await getCompanyAttendance(token, { startDate, endDate }, selectedUnit?.id);
              setAttendanceData(res?.results || []);
            })();
          }}
          isMonthOpen={isMonthOpen}
          onShiftsChanged={() => {
            // idem
            (async () => {
              const startDate = formatDateMonthSelected(monthAnchor, 'start');
              const endDate = formatDateMonthSelected(monthAnchor, 'end');
              const res = await getCompanyAttendance(token, { startDate, endDate }, selectedUnit?.id);
              setAttendanceData(res?.results || []);
            })();
          }}
          holidayToShow={[
            ...new Map(
                holidays
                .filter(h => h.date === modalDetailsData)
                .map(h => [h.name, h])
            ).values()
          ]}
        />
      )}
    </div>
  );
}


const SvgPattern = () => {
    return {
        backgroundColor: '#ffffff',
        backgroundImage: `url("data:image/svg+xml,%3Csvg width='48' height='32' viewBox='0 0 48 32' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%2322c55e' fill-opacity='0.13'%3E%3Cpath d='M27 32c0-3.314 2.686-6 6-6 5.523 0 10-4.477 10-10S38.523 6 33 6c-3.314 0-6-2.686-6-6h2c0 2.21 1.79 4 4 4 6.627 0 12 5.373 12 12s-5.373 12-12 12c-2.21 0-4 1.79-4 4h-2zm-6 0c0-3.314-2.686-6-6-6-5.523 0-10-4.477-10-10S9.477 6 15 6c3.314 0 6-2.686 6-6h-2c0 2.21-1.79 4-4 4C8.373 4 3 9.373 3 16s5.373 12 12 12c2.21 0 4 1.79 4 4h2z' /%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
        backgroundRepeat: 'repeat',
    };
};

const SvgHoliday = () => {
    return {
        backgroundColor: '#ffffff',
        backgroundImage: `url("data:image/svg+xml,%3Csvg width='48' height='32' viewBox='0 0 48 32' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23fed7aa' fill-opacity='0.4'%3E%3Cpath d='M27 32c0-3.314 2.686-6 6-6 5.523 0 10-4.477 10-10S38.523 6 33 6c-3.314 0-6-2.686-6-6h2c0 2.21 1.79 4 4 4 6.627 0 12 5.373 12 12s-5.373 12-12 12c-2.21 0-4 1.79-4 4h-2zm-6 0c0-3.314-2.686-6-6-6-5.523 0-10-4.477-10-10S9.477 6 15 6c3.314 0 6-2.686 6-6h-2c0 2.21-1.79 4-4 4C8.373 4 3 9.373 3 16s5.373 12 12 12c2.21 0 4 1.79 4 4h2z' /%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
        backgroundRepeat: 'repeat',
    };
};


const SvgHolidayWeekend = () => {
    return {
        backgroundColor: '#feead2',
        backgroundImage: `url("data:image/svg+xml,%3Csvg width='48' height='32' viewBox='0 0 48 32' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M27 32c0-3.314 2.686-6 6-6 5.523 0 10-4.477 10-10S38.523 6 33 6c-3.314 0-6-2.686-6-6h2c0 2.21 1.79 4 4 4 6.627 0 12 5.373 12 12s-5.373 12-12 12c-2.21 0-4 1.79-4 4h-2zm-6 0c0-3.314-2.686-6-6-6-5.523 0-10-4.477-10-10S9.477 6 15 6c3.314 0 6-2.686 6-6h-2c0 2.21-1.79 4-4 4C8.373 4 3 9.373 3 16s5.373 12 12 12c2.21 0 4 1.79 4 4h2z' /%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
        backgroundRepeat: 'repeat',
    };
};


const PatternInactiveEmployee = () => {
    return {
        backgroundColor: '#ffffff',
        backgroundImage: `url("data:image/svg+xml,%3Csvg width='6' height='6' viewBox='0 0 6 6' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%234f46e5' fill-opacity='1' fill-rule='evenodd'%3E%3Cpath d='M5 0h1L0 6V5zM6 5v1H5z'/%3E%3C/g%3E%3C/svg%3E")`,
        backgroundRepeat: 'repeat',
    };
};

const PatternNoWeekHours = () => {
    return {
        backgroundColor: '#fffbeb',
        cursor: 'pointer',
    };
};

const PatternNoCalendar = () => {
    return {
        backgroundColor: '#f1f5f9',
        cursor: 'pointer',
    };
};